/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import type { SeoMetadataResponseModel } from '@/api/types/seo-data-types';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import Head from 'next/head';
import { useMemo } from 'react';
import type { ProductReviewsData } from '../../interface';
import { replaceSEOMetadataPlaceholder } from '../../utils/replaceSEOMetadataPlaceholder';
import { generateReviewsSchema } from '../../utils/generateReviewsSchema';

const labelMap = {
  reviews: 'label_reviews',
};

export type ProductReviewsMetadataProps = {
  seoMetaData: SeoMetadataResponseModel | undefined;
  reviewsData: ProductReviewsData | undefined;
};

export function ProductReviewsMetadata({ seoMetaData, reviewsData }: ProductReviewsMetadataProps) {
  const { data: productDetails } = useProductDetails();
  const labels = useLabels(labelMap);
  const locale = useLocale();
  const productName = productDetails?.product?.itemDescription;
  const productImageUrl = productDetails?.product?.itemImageUrl;

  const reviewsSchema: string = useMemo(() => {
    if (!productDetails?.product || !reviewsData) {
      return '';
    }

    return JSON.stringify(generateReviewsSchema(productDetails.product, reviewsData));
  }, [reviewsData, productDetails]);

  return (
    <Head>
      {productName && !seoMetaData?.pageTitle ? (
        <title key="title">{`${productName} ${labels.reviews}`}</title>
      ) : null}
      {seoMetaData?.pageTitle ? (
        <title key="title">
          {replaceSEOMetadataPlaceholder(seoMetaData.pageTitle, productName)}
        </title>
      ) : null}
      {seoMetaData?.pageTitle && (
        <meta
          key="metaTitle"
          name="og:title"
          content={replaceSEOMetadataPlaceholder(seoMetaData.pageTitle, productName)}
        />
      )}

      {seoMetaData?.indexEnabledFlag ? (
        <>
          {seoMetaData.pageContentDescription && (
            <meta
              key="metaDescription"
              name="description"
              content={replaceSEOMetadataPlaceholder(
                seoMetaData.pageContentDescription,
                productName
              )}
            />
          )}
          {seoMetaData.currentPageUrl && (
            <meta key="metaUrl" property="og:url" content={seoMetaData.currentPageUrl} />
          )}

          {productImageUrl && (
            <meta key="metaImage" property="og:image" content={productImageUrl} />
          )}
          {seoMetaData.siteName && (
            <meta key="metaSiteName" property="og:site_name" content={seoMetaData.siteName} />
          )}
          <meta key="metaType" property="og:type" content="review" />
        </>
      ) : (
        <meta key="metaRobots" name="robots" content="noindex, follow" />
      )}

      {seoMetaData?.robotsContent ? (
        <meta key="metaRobots" name="robots" content={seoMetaData.robotsContent} />
      ) : null}

      {seoMetaData?.canonicalUrl && (
        <>
          <link key="linkCanonical" rel="canonical" href={seoMetaData.canonicalUrl} />
          {seoMetaData.alternateUrl && (
            <link
              key="linkAlternateLocale"
              rel="alternate"
              hrefLang={locale.toLowerCase()}
              href={seoMetaData.canonicalUrl}
            />
          )}
          {seoMetaData.alternateUrl && seoMetaData.langaugeLocale ? (
            <link
              key="linkAlternateObj"
              rel="alternate"
              hrefLang={seoMetaData.langaugeLocale}
              href={seoMetaData.alternateUrl}
            />
          ) : null}
        </>
      )}

      {reviewsSchema && (
        <script
          key="reviewsSchema"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: reviewsSchema }}
        />
      )}
    </Head>
  );
}
